/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Tag, Typography, breakpoints } from '@coursera/cds-core';
import { SuccessIcon } from '@coursera/cds-icons';

import CompletionWarningRenderer from 'bundles/degree-home/components/v3-dpm/components/CompletionWarningRenderer';
import type {
  UseCurriculum_DegreeCurriculumFragment as DegreeCurriculumProps,
  UseCurriculum_PbaCurriculumFragment as PBACurriculumProps,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/useCurriculum';
import { getCreditsCompletionText } from 'bundles/degree-home/components/v3-dpm/lib/completionUtils';
import { verifyDegreeCompletionStatus } from 'bundles/degree-home/components/v3-dpm/lib/gradeUtils';
import useProgressionCalculator from 'bundles/degree-home/components/v3-dpm/lib/useProgressionCalculator';
import { useIntl } from 'bundles/page/lib/useIntl';
import { MetadataTypography } from 'bundles/program-loggedin-shared/components/Metadata';
import { renderGpa } from 'bundles/program-loggedin-shared/libraries/gradeUtils';

import _t from 'i18n!nls/degree-home';

import { METADATA_SEPARATOR } from './ProgressBar/Legend';

const styles = {
  container: () => css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    border-top: 2px solid var(--cds-color-grey-50);
    padding: var(--cds-spacing-300) 0;
    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-300);
      flex-direction: column;
    }
  `,
  categoryHeaderContent: css`
    width: fit-content;
  `,
  completeTagContainer: () => css`
    ${breakpoints.down('xs')} {
      padding-top: var(--cds-spacing-300);
    }
  `,
};

interface CurriculumHeaderProps {
  curriculum: DegreeCurriculumProps | PBACurriculumProps;
}

const CurriculumHeader: React.FC<CurriculumHeaderProps> = ({ curriculum }) => {
  const { title, description, minCredits, minGpa, minPassingGradeForCourses } = curriculum;
  const intl = useIntl();

  const minCreditsText = intl.formatMessage(
    _t('{minCredits} {minCredits, plural, =1 { credit} other { credits}} required'),
    { minCredits }
  );
  const minGpaText = _t('Min GPA: #{gpa}', { gpa: renderGpa(minGpa) });
  const minGradeText = _t('Min Grade: #{grade}%', { grade: minPassingGradeForCourses });

  const metadataString = [minCreditsText, minGpaText, minGradeText].join(METADATA_SEPARATOR);

  const overallProgression = useProgressionCalculator();
  const isCurriculumCompleted = verifyDegreeCompletionStatus(curriculum, overallProgression);
  const curriculumProgression = overallProgression?.curriculums?.[curriculum.id];
  const completedCredits = curriculumProgression?.summary?.completed;
  const inProgressCredits = curriculumProgression?.summary?.inProgress;

  const creditsCompletionTextList = getCreditsCompletionText(completedCredits, inProgressCredits);
  const creditsCompletionText = creditsCompletionTextList.join(METADATA_SEPARATOR);

  const hasGradeWarning = curriculumProgression?.warnings?.grade;
  const hasGPAWarning = curriculumProgression?.warnings?.gpa;
  const curriculumGPA = curriculumProgression?.curriculumGPA;

  return (
    <div css={styles.container}>
      <div css={styles.categoryHeaderContent}>
        {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
        <Typography variant="h2semibold" component="h3">
          {title}
        </Typography>
        <MetadataTypography>{metadataString}</MetadataTypography>
        {creditsCompletionText && <MetadataTypography>{creditsCompletionText}</MetadataTypography>}
        {description && <MetadataTypography>{description}</MetadataTypography>}
        <CompletionWarningRenderer
          level="curriculum"
          hasGradeWarning={hasGradeWarning}
          hasGPAWarning={hasGPAWarning}
          gpa={curriculumGPA}
        />
      </div>
      {isCurriculumCompleted && (
        <div css={styles.completeTagContainer}>
          <Tag
            data-testid="complete-tag-curriculum-level"
            variant="status"
            priority="primary"
            color="success"
            icon={<SuccessIcon />}
          >
            {_t('Complete')}
          </Tag>
        </div>
      )}
    </div>
  );
};

export default CurriculumHeader;
